import React from 'react'
import { MerchantLocation } from '@slerp/controls'
import { Section } from 'components/Common'
import { StepStatus } from 'hooks/useStep'
import {
  LinkStoresButton,
  SummaryActionCard,
  SummaryText,
  LoyaltyAndTableGroup
} from './styles'
import {
  LINK_STORES_STEP_TITLE,
  LINK_STORES_STEP_DESCRIPTION,
  linkedSummary
} from './copy'
import { findStoreLink, isLeatConnected } from './utils'
import { ConnectStoresRow, LeatStore, LeatStoreLink } from './types'
import PushOrdersCard from './PushOrdersCard'
import StoreLinkTable from './StoreLinkTable'
import { useStoreLinkRows } from './useStoreLinkRows'
import LoadingPrimaryButton from './LoadingPrimaryButton'

interface StoreListStepProps {
  activeStores: MerchantLocation[]
  links: LeatStoreLink[]
  leatShops: LeatStore[]
  loading?: boolean
  onSave: (rows: ConnectStoresRow[]) => void
  onOpenConnectForm: () => void
}

const StoreListStep = ({
  activeStores,
  links,
  leatShops,
  loading,
  onSave,
  onOpenConnectForm
}: StoreListStepProps) => {
  const connectedStores = activeStores.filter((store) =>
    isLeatConnected(store.id, links)
  )
  const unconnectedStores = activeStores.filter(
    (store) => !connectedStores.includes(store)
  )

  const { rows, updateRow } = useStoreLinkRows(() =>
    connectedStores.reduce<Record<string, ConnectStoresRow>>((acc, store) => {
      const existing = findStoreLink(store.id, links)
      acc[store.id] = {
        storeId: store.id,
        leatStoreId: existing?.store_integration_settings.leat_store_id ?? null,
        leatOrderPushEnabled:
          existing?.store_integration_settings.leat_order_push_enabled ?? true
      }
      return acc
    }, {})
  )

  const rowList = connectedStores.map((store) => rows[store.id])
  const pushDisabledCount = rowList.filter(
    (row) => !row.leatOrderPushEnabled
  ).length

  return (
    <>
      <Section
        title={LINK_STORES_STEP_TITLE}
        description={LINK_STORES_STEP_DESCRIPTION}
        stepStatus={StepStatus.completed}
        stepNumber={2}
      >
        <LoyaltyAndTableGroup>
          <PushOrdersCard />

          <StoreLinkTable
            stores={connectedStores}
            rows={rows}
            leatShops={leatShops}
            loading={loading}
            allowNone
            onRowChange={updateRow}
          />

          {unconnectedStores.length > 0 && (
            <LinkStoresButton disabled={loading} onClick={onOpenConnectForm}>
              LINK STORES
            </LinkStoresButton>
          )}
        </LoyaltyAndTableGroup>
      </Section>

      <SummaryActionCard>
        <SummaryText>
          {linkedSummary(
            connectedStores.length,
            activeStores.length,
            pushDisabledCount
          )}
        </SummaryText>
        <LoadingPrimaryButton
          disabled={loading}
          loading={loading}
          onClick={() => onSave(rowList)}
        >
          SAVE
        </LoadingPrimaryButton>
      </SummaryActionCard>
    </>
  )
}

export default StoreListStep
